import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Avatar,
    Conversation,
} from '@chatscope/chat-ui-kit-react';
import InitialsAvatar from 'react-avatar';
import {
    collection,
    DocumentData,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    QueryConstraint,
    QueryDocumentSnapshot,
    startAfter,
    Timestamp,
    where,
} from 'firebase/firestore';
import InfiniteScroll from 'react-infinite-scroll-component';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import CreatorDMFilters from 'Components/creator-dm/CreatorDMFilters';
import FilterIcon from 'Components/icons/FilterIcon';
import { useFirebase } from 'contexts/FirebaseContext';
import {
    CreatorDmSession,
    CreatorDmSessionFilters,
    defaultCreatorDmSessionFilters,
} from 'types/creator-dm';

const PAGE_SIZE = 25;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export const toJsDate = (
    value?: Timestamp | { seconds: number; nanoseconds?: number } | null
): Date | null => {
    if (!value) return null;
    if (typeof (value as Timestamp).toDate === 'function') {
        return (value as Timestamp).toDate();
    }
    if (typeof (value as { seconds: number }).seconds === 'number') {
        return new Date((value as { seconds: number }).seconds * 1000);
    }
    return null;
};

export const isWithinLast24Hours = (
    value?: Timestamp | { seconds: number; nanoseconds?: number } | null
): boolean => {
    const date = toJsDate(value);
    if (!date) return false;
    return Date.now() - date.getTime() < TWENTY_FOUR_HOURS_MS;
};

export const canReplyToSession = (session: CreatorDmSession | null | undefined): boolean => {
    if (!session?.last_message_id) return false;
    return isWithinLast24Hours(session.last_message_at);
};

const formatListTimestamp = (
    value?: Timestamp | { seconds: number; nanoseconds?: number } | null
): string => {
    const date = toJsDate(value);
    if (!date) return '';
    const m = moment(date);
    if (m.isSame(moment(), 'day')) return m.format('h:mma');
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
    return m.format('DD/MM/YY');
};

const getCreatorLabel = (session: CreatorDmSession) =>
    session.social_account?.name || session.social_account?.handle || 'Unknown creator';

const hasActiveFilters = (filters: CreatorDmSessionFilters) =>
    Boolean(filters.status || filters.creator_responded || filters.control);

const matchesCreatorResponded = (
    session: CreatorDmSession,
    creatorResponded?: CreatorDmSessionFilters['creator_responded']
) => {
    if (!creatorResponded) return true;
    const responded = Boolean(session.last_message_id);
    return creatorResponded === 'y' ? responded : !responded;
};

const SessionConversation: React.FC<{ session: CreatorDmSession; active: boolean }> = ({
    session,
    active,
}) => {
    const name = getCreatorLabel(session);
    const handle = session.social_account?.handle ? `@${session.social_account.handle}` : '';
    const agentLabel = session.agent ? ` ${session.agent}` : '';

    return (
        <Conversation
            name={name}
            info={agentLabel}
            lastSenderName={formatListTimestamp(session.last_message_at) || handle}
            active={active}
        >
            {session.social_account?.profile_picture_url ? (
                <Avatar src={session.social_account.profile_picture_url} name={name} />
            ) : (
                <Avatar>
                    <InitialsAvatar name={name} maxInitials={2} size="40" round />
                </Avatar>
            )}
        </Conversation>
    );
};

type CreatorDMLayoutProps = {
    children: React.ReactElement;
    setActiveSession?: (session: CreatorDmSession | null) => void;
    setListLoaded?: (loaded: boolean) => void;
};

const CreatorDMLayout: React.FC<CreatorDMLayoutProps> = ({
    children,
    setActiveSession,
    setListLoaded,
}) => {
    const { sessionId: activeSessionId } = useParams();
    const [sessions, setSessions] = useState<CreatorDmSession[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState<CreatorDmSessionFilters>(defaultCreatorDmSessionFilters);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const { db, firebaseUser } = useFirebase();
    const navigate = useNavigate();
    const lastVisibleRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

    const firestoreConstraints = useMemo(() => {
        const constraints: QueryConstraint[] = [];
        if (filters.status) {
            constraints.push(where('status', '==', filters.status));
        }
        if (filters.control) {
            constraints.push(where('control', '==', filters.control));
        }
        return constraints;
    }, [filters.status, filters.control]);

    const mapDocs = useCallback(
        (docs: QueryDocumentSnapshot<DocumentData>[]): CreatorDmSession[] =>
            docs
                .map((d) => ({
                    ...(d.data() as Omit<CreatorDmSession, 'id'>),
                    id: d.id,
                }))
                .filter((session) => matchesCreatorResponded(session, filters.creator_responded)),
        [filters.creator_responded]
    );

    useEffect(() => {
        if (!activeSessionId && sessions.length > 0 && window.innerWidth >= 768) {
            navigate(`/creator-dm/${sessions[0].id}`);
        }
    }, [activeSessionId, sessions, navigate]);

    useEffect(() => {
        if (!firebaseUser || !db) return;

        setListLoaded?.(false);
        lastVisibleRef.current = null;

        const q = query(
            collection(db, 'creator_dm_sessions'),
            ...firestoreConstraints,
            orderBy('last_message_at', 'desc'),
            limit(PAGE_SIZE)
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                if (!snapshot.empty) {
                    const docs = mapDocs(snapshot.docs);
                    setSessions(docs);
                    lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1];
                    setHasMore(snapshot.docs.length === PAGE_SIZE);
                } else {
                    setSessions([]);
                    setHasMore(false);
                }
                setListLoaded?.(true);
            },
            (error) => {
                console.error('Failed to subscribe to creator_dm_sessions', error);
                setListLoaded?.(true);
            }
        );

        return () => unsubscribe();
    }, [firebaseUser, db, setListLoaded, firestoreConstraints, mapDocs]);

    const fetchNextPage = useCallback(async () => {
        const lastVisible = lastVisibleRef.current;
        if (!lastVisible || !db) return;

        const q = query(
            collection(db, 'creator_dm_sessions'),
            ...firestoreConstraints,
            orderBy('last_message_at', 'desc'),
            startAfter(lastVisible),
            limit(PAGE_SIZE)
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            const docs = mapDocs(snapshot.docs);
            setSessions((prev) => {
                const existingIds = new Set(prev.map((s) => s.id));
                return [...prev, ...docs.filter((s) => !existingIds.has(s.id))];
            });
            lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1];
            setHasMore(snapshot.docs.length === PAGE_SIZE);
        } else {
            setHasMore(false);
        }
    }, [db, firestoreConstraints, mapDocs]);

    useEffect(() => {
        if (!activeSessionId) {
            setActiveSession?.(null);
            return;
        }
        const active = sessions.find((s) => s.id === activeSessionId);
        if (active) {
            setActiveSession?.(active);
        }
    }, [activeSessionId, sessions, setActiveSession]);

    return (
        <div className="container-fluid h-100 border-bottom-1 creator-dm-page">
            <div className="row h-100">
                <div
                    className={`col-12 col-md-4 border-end p-0 ${
                        activeSessionId ? 'd-none d-md-flex' : 'd-flex'
                    } flex-column`}
                >
                    <div className="p-3 border-bottom bg-light chat-ui-header d-flex align-items-center justify-content-between gap-2">
                        <h5 className="mb-0">Creator DMs</h5>
                        <button
                            type="button"
                            className={`btn btn-sm btn-white ${hasActiveFilters(filters) ? 'active' : ''}`}
                            onClick={() => setFiltersOpen(true)}
                        >
                            <FilterIcon className="me-1" /> Filters
                        </button>
                    </div>
                    <div className="chat-container">
                        <PerfectScrollbar id="creatorDmScrollableDiv">
                            <InfiniteScroll
                                dataLength={sessions.length}
                                next={fetchNextPage}
                                className="row g-3 overflow-x-hidden m-0"
                                hasMore={hasMore}
                                scrollableTarget="creatorDmScrollableDiv"
                                loader={<></>}
                            >
                                {sessions.map((session) => (
                                    <div
                                        className="m-0 p-0"
                                        key={session.id}
                                        onClick={() => navigate(`/creator-dm/${session.id}`)}
                                    >
                                        <SessionConversation
                                            session={session}
                                            active={activeSessionId === session.id}
                                        />
                                    </div>
                                ))}
                                {sessions.length === 0 && (
                                    <div className="d-flex w-100 chat-container m-0 justify-content-center align-items-center">
                                        <div className="length-zero m-0">
                                            <h3>
                                                {hasActiveFilters(filters)
                                                    ? 'No DMs match these filters'
                                                    : 'No recent DMs!'}
                                            </h3>
                                        </div>
                                    </div>
                                )}
                            </InfiniteScroll>
                        </PerfectScrollbar>
                    </div>
                </div>

                <div
                    className={`col-12 col-md-8 ${
                        !activeSessionId ? 'd-none d-md-flex' : 'd-flex'
                    } flex-column p-0`}
                >
                    <div className="flex-grow-1 overflow-auto chat-window">{children}</div>
                </div>
            </div>

            <CreatorDMFilters
                isOpen={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                defaultFilters={filters}
                onApply={setFilters}
            />
        </div>
    );
};

export default CreatorDMLayout;
